<template>
  <div class="pricing-form">
    <h1 class="title">{{ $t("MEMBERSHIP_PRICING_TITLE") }}</h1>
    <h4 class="description">{{ $t("MEMBERSHIP_PRICING_DESCRIPTION") }}</h4>
    <ShForm class="calculate-form">
      <ShSelect
        :label="$t('MEMBERSHIP_TYPE_SELECT_LABEL')"
        id="subscriptionType"
        name="subscriptionType"
        v-model="subscriptionType"
        v-validate="'required'"
        :defaultLabel="$t('SELECT_OPTION')"
        :options="subscriptionTypes"
        :errorMessage="errors.first('subscriptionType')"
      />
      <ShSelect
        v-if="showCommunity"
        :label="$t('MEMBERSHIP_COMMUNITY_SELECT_LABEL')"
        id="venue"
        name="venue"
        v-model="venue"
        v-validate="'required'"
        :defaultLabel="$t('SELECT_OPTION')"
        :options="venues"
        :errorMessage="errors.first('venue')"
      />
      <template v-if="showNextSelects">
        <ShSelect
          :label="$t('MEMBERSHIP_ARE_YOU_U27')"
          id="membershipType"
          name="membershipType"
          v-model="membershipType"
          v-validate="'required'"
          :defaultLabel="$t('SELECT_OPTION')"
          :options="membershipTypes"
          :errorMessage="errors.first('membershipType')"
        />
        <ShSelect
          :label="$t('MEMBERSHIP_HOW_TO_PAY')"
          id="paymentTerm"
          name="paymentTerm"
          v-model="paymentTerm"
          v-validate="'required'"
          :defaultLabel="$t('SELECT_OPTION')"
          :options="paymentTerms"
          :errorMessage="errors.first('paymentTerm')"
        />
      </template>
      <template #submit>
        <ShButton :outline="true" :fullWidth="true" @click="calculatePlan">
          {{ $t("MEMBERSHIP_CALCULATE_CTA_LABEL") }}
        </ShButton>
      </template>
    </ShForm>
    <div v-if="showStart" class="start-block">
      <div v-if="isEvery" class="information">
        <p>
          {{ $t("APPLICATION_FORM_PACKAGE_EVERY_HOUSE_TITLE") }}<br /><br />
          {{ $t("APPLICATION_FORM_PACKAGE_EVERY_HOUSE_SUPPORTING_LINE_1")
          }}<br /><br />
          {{ $t("APPLICATION_FORM_PACKAGE_EVERY_HOUSE_SUPPORTING_LINE_2") }}
        </p>
      </div>
      <div v-if="isLocal" class="information">
        <p>
          {{ $t("APPLICATION_FORM_PACKAGE_LOCAL_HOUSE_TITLE") }}<br /><br />
          {{
            $t("APPLICATION_FORM_PACKAGE_LOCAL_HOUSE_SUPPORTING", {
              venue: venueName,
            })
          }}
        </p>
      </div>
      <div class="action">
        <div class="price-description">
          <span class="cost">{{ paymentTermText }} {{ price }}</span>
          <span class="fee"
            >{{ $t("MEMBERSHIP_PRICE_REGISTRATION") }} {{ fee }}</span
          >
          <span class="grey">{{
            $t("APPLICATION_FORM_PACKAGE_PRICE_REGISTRATION_SUPPORTING")
          }}</span>
          <span v-if="taxText" class="tax grey">{{ taxText }}</span>
        </div>
        <ShButton class="start-btn" :fullWidth="true" :to="ctaTo">{{
          ctaText
        }}</ShButton>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { ShForm, ShSelect } from '@sohohouse/design-system'
import { formatPrice, getTermCalculationMultiplier } from '~/helpers'
import { MEMBERSHIP_TYPES, SUBSCRIPTION_TYPES, TAX_TYPES } from '~/constants'
import { alphabetiseVenues } from '~/utils/alphabetiseVenues'

const { mapGetters: mapApplicationGetters } = createNamespacedHelpers(
  'applications/applications'
)
const { mapGetters: mapProductGetters } = createNamespacedHelpers(
  'applications/products'
)
export const EXCLUDED_VENUES = ['SHW_ACTIVE', 'SHK_ACTIVE']

export default {
  components: {
    ShForm,
    ShSelect
  },

  $_veeValidate: {
    validator: 'new'
  },

  data () {
    return {
      price: 0,
      fee: 0,
      membershipType: '',
      subscriptionType: '',
      venue: '',
      venueName: '',
      venues: [],
      paymentTerm: '',
      paymentTerms: [],
      paymentTermText: '',
      taxText: '',
      showCommunity: false,
      showNextSelects: false,
      showStart: false
    }
  },

  mounted () {
    this.$validator.reset()
    this.$validator.localize(this.$i18n.locale, {
      messages: {
        required: this.$t('INQUIRY_ERROR_REQUIRED_FIELD')
      }
    })
  },

  async fetch () {
    const { dispatch } = this.$store

    await dispatch('applications/products/fetchProducts')
    await dispatch('applications/applications/fetchApplications')
  },

  computed: {
    ...mapApplicationGetters(['applications']),
    ...mapProductGetters(['products']),
    subscriptionTypes () {
      return [
        {
          value: SUBSCRIPTION_TYPES.LOCAL,
          label: this.$t('APPLICATION_FORM_PACKAGE_PRICE_LOCAL')
        },
        {
          value: SUBSCRIPTION_TYPES.EVERY,
          label: this.$t('APPLICATION_FORM_PACKAGE_PRICE_EVERY')
        }
      ]
    },
    membershipTypes () {
      return [
        {
          value: MEMBERSHIP_TYPES.UNDER_27,
          label: this.$t('MEMBERSHIP_OPTION_YES')
        },
        {
          value: MEMBERSHIP_TYPES.REGULAR,
          label: this.$t('MEMBERSHIP_OPTION_NO')
        }
      ]
    },
    isEvery () {
      return this.subscriptionType === SUBSCRIPTION_TYPES.EVERY
    },
    isLocal () {
      return this.subscriptionType === SUBSCRIPTION_TYPES.LOCAL
    },
    hasApplication () {
      return !!this.applications.length
    },
    applicationInProgress () {
      return this.hasApplication && !this.applications[0].submissions.length
    },
    ctaText () {
      return this.$t(
        this.applicationInProgress
          ? 'MORE_APPLICATIONS_CARD_CTA_CONTINUE'
          : 'MORE_APPLICATIONS_CREATE_CTA'
      )
    },
    ctaTo () {
      const { subscriptionType, paymentTerm, venue } = this

      const query = { subscriptionType, paymentTerm, venueId: venue }

      if (this.applicationInProgress) {
        return this.localePath({
          name: 'apply-id-package',
          params: { id: this.applications[0].id },
          query
        })
      }

      return this.localePath({ name: 'apply', query })
    }
  },

  watch: {
    subscriptionType (value, prevValue) {
      if (value !== prevValue) {
        this.$validator.reset()
        this.showStart = false
        this.venue = ''
        this.membershipType = ''
        this.paymentTerm = ''
        this.venues = this.setVenueOptions(value)
        this.showCommunity = true
        this.showNextSelects = false
      }
    },
    venue (value, prevValue) {
      if (value && value !== prevValue) {
        this.$validator.reset()
        this.showStart = false
        this.membershipType = ''
        this.paymentTerm = ''
        this.paymentTerms = this.setPaymentTermOptions(value)
        this.showNextSelects = true
      }
    }
  },

  methods: {
    setVenueOptions (type) {
      const uniqueVenues = this.products
        .filter(({ subscriptionType, venue }) => (
          !EXCLUDED_VENUES.includes(venue.id)
          && subscriptionType === type))
        .reduce((result, current) => {
          const { venue } = current

          return { ...result, [venue.id]: venue }
        }, {})

      const venues = alphabetiseVenues(Object.values(uniqueVenues))

      return venues.map(({ id, name }) => ({
        value: id,
        label: name
      }))
    },
    setPaymentTermOptions (venueId) {
      const terms = this.products.find(({ subscriptionType, venue }) => (
        subscriptionType === this.subscriptionType && venue.id === venueId
      )).validPaymentTerms

      return terms.map(term => ({
        value: term,
        label: this.$t(
          `APPLICATION_FORM_PACKAGE_PRICE_TERM_${term.toUpperCase()}`
        )
      }))
    },
    taxDescription (product) {
      const { taxRate, validTaxType } = product

      if (validTaxType === 'NOT_APPLICABLE') return ''

      const taxType = this.$t(TAX_TYPES[validTaxType])

      return this.$t('APPLICATION_FORM_PACKAGE_PRICE_TERM_TAX', {
        tax: taxRate,
        type: taxType
      })
    },
    registrationPrice (product) {
      const { registrationPriceCents, registrationPriceCurrency } = product

      return formatPrice(
        registrationPriceCents,
        registrationPriceCurrency,
        this.$n.bind(this)
      )
    },
    membershipPrice (product, term) {
      const { annualPriceCents, annualPriceCurrency } = product

      const price = annualPriceCents / getTermCalculationMultiplier(term)

      return formatPrice(price, annualPriceCurrency, this.$n.bind(this))
    },
    async calculatePlan () {
      const canCalculate = await this.$validator.validateAll()

      if (!canCalculate) return

      const filteredProduct = this.products.find(product => (
        product.subscriptionType === this.subscriptionType
          && product.membershipType === this.membershipType
          && product.venue.id === this.venue
      ))

      this.venueName = filteredProduct.venue.name
      this.paymentTermText = this.$t(
        `MEMBERSHIP_COST_TERM_${this.paymentTerm.toUpperCase()}`
      )
      this.taxText = this.taxDescription(filteredProduct)
      this.fee = this.registrationPrice(filteredProduct)
      this.price = this.membershipPrice(filteredProduct, this.paymentTerm)
      this.showStart = true
    }
  }
}
</script>

<style lang="scss" scoped>
.pricing-form {
  max-width: 100%;
  margin: $space-090 auto 0;
  padding: $space-050;
  display: flex;
  flex-direction: column;

  @include tablet {
    max-width: 50%;
    padding: 0;
  }

  @include desktop {
    max-width: 35%;
  }

  .title {
    @include font("080");

    @include tablet {
      @include font("090");
    }
  }

  .description {
    margin-top: $space-040;
    @include font("050");
  }

  .calculate-form {
    margin-top: $space-070;
  }

  .start-block {
    margin-top: $space-080;

    .information {
      padding: $space-050 $space-050 $space-060;
      border: 1px solid $color-gray-7;
    }

    .action {
      margin-top: $space-070;
      display: flex;
      flex-direction: column;

      .price {
        @include font("080");
      }

      .price-description {
        display: flex;
        flex-direction: column;
        @include font("040");

        .cost {
          @include font("080");
        }

        .grey {
          color: $color-slate-gray;
        }

        .fee,
        .tax {
          margin-top: $space-050;
        }
      }

      .start-btn {
        margin-top: $space-070;
      }
    }
  }
}
</style>
